import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Query } from 'react-apollo';
import Box from '@material-ui/core/Box';
import Spin from 'components/common/Spin';
import { GET_LC_STUDENTS } from 'graphql/lc/students';
import Helmet from 'react-helmet';
import { Chip, Typography } from '@material-ui/core';
import MaterialTable from 'material-table';
import { toUserTimezone } from 'utils';
import Switch from 'components/form/Switch';
import PRODUCTS from 'constant/product-types';
import moment from 'moment';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const TRIAL_LOOKUP = { true: 'Yes', false: 'No' };

function pluck_gradYear(array, key) {
  return array?.students.map((o) => o[key]);
}

const Students = ({ history, userId }) => {
  const user = useSelector((s) => s.user.user);
  const [showPast, setShowPast] = useState(false);

  return (
    <Query
      query={GET_LC_STUDENTS}
      variables={{
        user_id: userId || user.id,
        date: moment().format('YYYY-MM-DD'),
      }}
    >
      {({ data, loading }) => {
        if (!data) return <Spin spinning />;
        let gradYear = pluck_gradYear(data, 'graduation_year');
        let unique_gradYear = [...new Set(gradYear)];
        let filtered_gradYear = unique_gradYear.filter((el) => el !== null);
        const YEAR_OPTIONS = filtered_gradYear.map((i) => ({
          label: i,
          value: i,
        }));
        const newObj = YEAR_OPTIONS.reduce(
          (obj, item) => ((obj[item.label] = item.value), obj),
          {},
        );

        const columns = [
          {
            title: 'Preferred Name',
            field: 'user_preferred_name',
            editable: 'never',
            filtering: false,
          },
          {
            title: 'First Name',
            field: 'user_first_name',
            editable: 'never',
            defaultSort: 'asc',
            filtering: false,
          },
          {
            title: 'Last Name',
            field: 'user_last_name',
            editable: 'never',
            filtering: false,
          },
          {
            title: 'Product',
            field: 'product_type',
            render: (row) =>
              row.product_type && (
                <Chip
                  variant={'outlined'}
                  label={PRODUCTS_LOOKUP[row.product_type]}
                  color="primary"
                  size="small"
                />
              ),
            lookup: PRODUCTS_LOOKUP,
            filtering: false,
          },
          {
            title: 'Graduation year',
            field: 'graduation_year',
            type: 'number',
            lookup: newObj,
            filterPlaceholder: 'Select Year',
            filterOnItemSelect: true,
          },
          {
            title: 'Group',
            field: 'group_student.group.name',
            render: (row) =>
              row.group_student && (
                <Chip
                  size="small"
                  variant={'outlined'}
                  color={'secondary'}
                  label={row.group_student.group.name}
                />
              ),
            filtering: false,
          },
          {
            title: 'Trial',
            field: 'is_trial',
            render: (row) => (row.is_trial ? 'Yes' : 'No'),
            lookup: TRIAL_LOOKUP,
            filtering: false,
          },
          {
            title: 'Trial end date',
            field: 'trial_end_date',
            render: (row) =>
              row.is_trial &&
              row.trial_end_date &&
              `${toUserTimezone(row.trial_end_date).format('L')}`,
            filtering: false,
          },
          {
            title: 'Applications submitted',
            field: 'app_submitted.aggregate.count',
            render: (row) => `${row.app_submitted?.aggregate?.count}`,
            filtering: false,
          },
          {
            title: 'Colleges in school list',
            field: 'colleges_aggregate.aggregate.count',
            render: (row) => `${row?.colleges_aggregate?.aggregate?.count}`,
            filtering: false,
          },
          {
            title: 'Essays written',
            field: 'documents_aggregate.aggregate.count',
            render: (row) => `${row?.documents_aggregate?.aggregate?.count}`,
            filtering: false,
          },
          {
            title: 'Program end date',
            field: 'subscriptions[0].end_date',
            render: (row) =>
              row?.subscriptions?.length
                ? toUserTimezone(row.subscriptions[0].end_date).format('L')
                : '',
            filtering: false,
          },
        ];
        const filteredRows = showPast
          ? data?.students.filter((f) => !f.has_active_subscription)
          : data?.students;

        return (
          <React.Fragment>
            <Helmet title="Students" />
            <MaterialTable
              title={
                <Box ml={1}>
                  <Switch
                    label="Show past students"
                    color="primary"
                    checked={showPast}
                    onChange={() => setShowPast(!showPast)}
                  />
                </Box>
              }
              isLoading={loading}
              columns={columns}
              data={filteredRows}
              options={{
                pageSize: 10,
                filtering: true,
              }}
              actions={[
                {
                  icon: 'visibility',
                  tooltip: 'View Student Profile',
                  onClick: (event, rowData) =>
                    history.push(`/students/${rowData.user_id}/timeline`),
                },
                (row) => {
                  if (!row.has_active_subscription) return null;
                  return {
                    icon: 'videocam',
                    tooltip: 'Open conference room',
                    iconProps: { color: 'secondary' },
                    onClick: (event, rowData) =>
                      rowData.group_student
                        ? history.push(
                          `/meeting/group/${rowData.group_student.group_id}`,
                        )
                        : history.push(`/meeting/${rowData.user_cognito_sub}`),
                  };
                },
              ]}
              localization={{
                body: {
                  emptyDataSourceMessage: 'No students are assigned to you',
                },
              }}
            />
          </React.Fragment>
        );
      }}
    </Query>
  );
};

export default Students;
